package org.teddapp.project.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontVariation
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.Font
import org.jetbrains.compose.resources.FontResource
import tedd_app.shared.generated.resources.Res
import tedd_app.shared.generated.resources.bricolage_grotesque
import tedd_app.shared.generated.resources.geist
import tedd_app.shared.generated.resources.geist_mono

/**
 * Typographie du design system : trois familles variables embarquées (licence OFL,
 * fichiers dans composeResources/font).
 *
 * - Geist (`body`) : toute l'interface. Fournie par défaut à chaque `Text` via [TeddTheme].
 * - Bricolage Grotesque (`display`) : titres d'écran et de carte. Jamais sous 18 sp.
 * - Geist Mono (`mono`) : minuteurs, montants, heures, raccourcis, compteurs —
 *   toujours en chiffres tabulaires.
 */
class TeddFonts(val body: FontFamily, val display: FontFamily, val mono: FontFamily = FontFamily.Monospace)

val LocalTeddFonts = staticCompositionLocalOf { TeddFonts(FontFamily.Default, FontFamily.Default, FontFamily.Monospace) }

/** Graisses que l'échelle emploie. Une police variable les rend toutes depuis un seul fichier. */
private val Weights = listOf(400, 500, 520, 560, 600, 650, 700)

@Composable
private fun variableFamily(resource: FontResource): FontFamily = FontFamily(
    Weights.map { weight ->
        Font(
            resource,
            weight = FontWeight(weight),
            variationSettings = FontVariation.Settings(FontVariation.weight(weight)),
        )
    },
)

@Composable
fun rememberTeddFonts(): TeddFonts = TeddFonts(
    body = variableFamily(Res.font.geist),
    display = variableFamily(Res.font.bricolage_grotesque),
    mono = variableFamily(Res.font.geist_mono),
)

/**
 * Échelle typographique du design system. Les couleurs ne sont pas incluses : elles se
 * posent à l'appel. Une seule graisse forte par bloc, jamais de texte justifié.
 */
object TeddType {
    @Composable @ReadOnlyComposable
    private fun display(size: Int, line: Int, weight: Int, tracking: Float) = TextStyle(
        fontFamily = LocalTeddFonts.current.display,
        fontSize = size.sp,
        lineHeight = line.sp,
        fontWeight = FontWeight(weight),
        letterSpacing = tracking.em,
    )

    @Composable @ReadOnlyComposable
    private fun sans(size: Int, line: Int, weight: Int, tracking: Float = 0f) = TextStyle(
        fontFamily = LocalTeddFonts.current.body,
        fontSize = size.sp,
        lineHeight = line.sp,
        fontWeight = FontWeight(weight),
        letterSpacing = tracking.em,
    )

    @Composable @ReadOnlyComposable
    private fun mono(size: Int, line: Int, weight: Int, tracking: Float = 0f) = TextStyle(
        fontFamily = LocalTeddFonts.current.mono,
        fontSize = size.sp,
        lineHeight = line.sp,
        fontWeight = FontWeight(weight),
        letterSpacing = tracking.em,
        fontFeatureSettings = "tnum",
    )

    // ── Titres (Bricolage Grotesque) ──────────────────────────────────
    /** 44/48. Titre d'écran : la salutation d'Aujourd'hui. */
    val DisplayLg: TextStyle @Composable @ReadOnlyComposable get() = display(44, 48, 650, -0.03f)
    /** 28/34. Titre de section, titre d'écran courant. */
    val Title1: TextStyle @Composable @ReadOnlyComposable get() = display(28, 34, 600, -0.02f)
    /** 20/26. Titre de carte et de panneau. */
    val Title2: TextStyle @Composable @ReadOnlyComposable get() = display(20, 26, 600, -0.01f)
    /** 26/34. Citation ou question posée en grand. */
    val Quote: TextStyle @Composable @ReadOnlyComposable get() = display(26, 34, 500, -0.015f)

    // ── Texte (Geist) ─────────────────────────────────────────────────
    /** 17/26. Notes en lecture, introductions. */
    val BodyLg: TextStyle @Composable @ReadOnlyComposable get() = sans(17, 26, 400)
    /** 15/22. Texte d'interface par défaut, titres de tâche. */
    val Body: TextStyle @Composable @ReadOnlyComposable get() = sans(15, 22, 400)
    /** 15/22 appuyé. Accent dans une ligne, valeurs clés. */
    val BodyStrong: TextStyle @Composable @ReadOnlyComposable get() = sans(15, 22, 560)
    /** 13/18. Libellés de champ, boutons compacts, onglets, navigation. */
    val Label: TextStyle @Composable @ReadOnlyComposable get() = sans(13, 18, 520)
    /** 12/16. Métadonnées, horodatages, aides de champ. */
    val Caption: TextStyle @Composable @ReadOnlyComposable get() = sans(12, 16, 400)
    /** 11/14 espacé. Petits en-têtes de groupe, à écrire en capitales. */
    val Overline: TextStyle @Composable @ReadOnlyComposable get() = sans(11, 14, 600, 0.08f)

    // ── Données (Geist Mono, chiffres tabulaires) ─────────────────────
    /** 72/72. Minuteur Pomodoro. */
    val Timer: TextStyle @Composable @ReadOnlyComposable get() = mono(72, 72, 500, -0.04f)
    /** 32/36. Solde, valorisation d'actif. */
    val AmountLg: TextStyle @Composable @ReadOnlyComposable get() = mono(32, 36, 500, -0.02f)
    /** 15/22. Montants dans les listes. */
    val Amount: TextStyle @Composable @ReadOnlyComposable get() = mono(15, 22, 500)
    /** 12/16. Raccourcis clavier, heures, compteurs, séries. */
    val MonoSm: TextStyle @Composable @ReadOnlyComposable get() = mono(12, 16, 400)

    // ── Styles propres à l'application, entre deux crans de l'échelle ──
    /** 26/30 mono. Nombre mis en avant dans une carte, sous [AmountLg]. */
    val Numeral: TextStyle @Composable @ReadOnlyComposable get() = mono(26, 30, 500, -0.02f)
    /** 13/18 mono. Nombre courant dans une ligne dense. */
    val Mono: TextStyle @Composable @ReadOnlyComposable get() = mono(13, 18, 400)
    /** 15/22 appuyé. Titre de section dans le contenu. */
    val Heading: TextStyle @Composable @ReadOnlyComposable get() = sans(15, 22, 600)
    /** Chiffres alignés en colonnes, dans la police d'interface. */
    val Tabular: TextStyle @Composable @ReadOnlyComposable get() = Body.copy(fontFeatureSettings = "tnum")
}
